import * as mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

import { coordinateSearchResults } from './coordinate-query';

/**
 * Search field + "my location" button for the map dialog.
 *
 * Kept out of `map-select-dialog.component.ts` so the wiring is testable: the dialog builds a
 * real `mapboxgl.Map` in `ngOnInit`, which needs WebGL and cannot be instantiated under jsdom.
 *
 * Selecting a result only moves the camera. It draws nothing and never moves the site marker —
 * marking the farm stays a deliberate second step.
 */

/** Zoom the map animates to for a result that carries no bounding box (i.e. every coordinate). */
export const SEARCH_RESULT_ZOOM = 15;

export interface MapSearchTexts {
  /** Placeholder inside the search field. */
  placeholder: string;
  /** Shown in the dropdown when a query matches nothing. */
  noResults: string;
  /** Shown when the geocoding request itself fails. */
  searchFailed: string;
  /** Shown when the search field could not be created at all (e.g. a malformed access token). */
  searchUnavailable: string;
  locationDenied: string;
  locationUnavailable: string;
  locationTimeout: string;
  locationFailed: string;
}

/** Translation keys for every string this module can surface. */
export const MAP_SEARCH_TEXT_KEYS: Record<keyof MapSearchTexts, string> = {
  placeholder: 'Search_for_a_place_or_coordinates_tr',
  noResults: 'No_results_found_tr',
  searchFailed: 'Search_failed_Please_try_again_tr',
  searchUnavailable: 'Map_search_is_currently_unavailable_tr',
  locationDenied: 'Location_permission_denied_Please_allow_location_access_in_your_browser_tr',
  locationUnavailable: 'Your_location_is_currently_unavailable_tr',
  locationTimeout: 'Getting_your_location_took_too_long_Please_try_again_tr',
  locationFailed: 'Could_not_determine_your_location_tr',
};

/** English copy, used until (or when) a translation resolves. */
export const MAP_SEARCH_FALLBACK_TEXTS: MapSearchTexts = {
  placeholder: 'Search for a place or coordinates',
  noResults: 'No results found',
  searchFailed: 'Search failed. Please try again.',
  searchUnavailable: 'Map search is currently unavailable.',
  locationDenied: 'Location permission denied. Please allow location access in your browser.',
  locationUnavailable: 'Your location is currently unavailable.',
  locationTimeout: 'Getting your location took too long. Please try again.',
  locationFailed: 'Could not determine your location.',
};

/** Strings the geocoder renders into its own DOM, so they must already be translated. */
export type MapSearchRenderedTexts = Pick<MapSearchTexts, 'placeholder' | 'noResults' | 'searchFailed'>;

export interface MapSearchControlsOptions {
  accessToken: string;
  /** Site coordinates, used to bias search results towards the farm. */
  center?: { lat?: number | null; lng?: number | null };
  /** UI language, forwarded so Mapbox returns place names in it. */
  language?: string;
  texts?: Partial<MapSearchRenderedTexts>;
  /**
   * Called with a translation KEY, not a message — the caller runs it through its own toast
   * helper, which is what translates it.
   */
  onError: (messageKey: string) => void;
  /** Test seam: a stand-in for `navigator.geolocation`. */
  geolocation?: Geolocation;
}

export interface MapSearchControlsHandle {
  /** `null` when the control could not be created — the map itself still works. */
  geocoder: MapboxGeocoder | null;
  geolocate: mapboxgl.GeolocateControl | null;
  remove(): void;
}

/** `GeolocationPositionError` codes; the constants live on the instance, not the global scope. */
const PERMISSION_DENIED = 1;
const POSITION_UNAVAILABLE = 2;
const TIMEOUT = 3;

function geolocationErrorKey(err: unknown): string {
  switch ((err as GeolocationPositionError | undefined)?.code) {
    case PERMISSION_DENIED:
      return MAP_SEARCH_TEXT_KEYS.locationDenied;
    case POSITION_UNAVAILABLE:
      return MAP_SEARCH_TEXT_KEYS.locationUnavailable;
    case TIMEOUT:
      return MAP_SEARCH_TEXT_KEYS.locationTimeout;
    default:
      return MAP_SEARCH_TEXT_KEYS.locationFailed;
  }
}

function toProximity(center?: MapSearchControlsOptions['center']): MapboxGeocoder.LngLatLiteral | undefined {
  const lat = center?.lat;
  const lng = center?.lng;
  if (typeof lat !== 'number' || typeof lng !== 'number') return undefined;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
  return { latitude: lat, longitude: lng };
}

const escapeHtml = (text: string) =>
  text.replace(
    /[&<>"']/g,
    (c) => (({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }) as Record<string, string>)[c]
  );

/**
 * The plugin hardcodes its English dropdown messages with no option to override them, so the
 * two we surface are re-pointed on the instance. Guarded: if the internals ever move, the
 * plugin's own English copy simply stays.
 */
function translateGeocoderMessages(geocoder: MapboxGeocoder, texts: MapSearchRenderedTexts): void {
  const internals = geocoder as unknown as {
    _renderMessage?: (msg: string) => void;
    _renderNoResults?: () => void;
    _renderError?: () => void;
  };
  if (typeof internals._renderMessage !== 'function') return;

  const render = internals._renderMessage.bind(geocoder);
  internals._renderNoResults = () =>
    render(
      `<div class='mapbox-gl-geocoder--error mapbox-gl-geocoder--no-results'>${escapeHtml(texts.noResults)}</div>`
    );
  internals._renderError = () =>
    render(`<div class='mapbox-gl-geocoder--error'>${escapeHtml(texts.searchFailed)}</div>`);
}

/**
 * Add the search field and the location button to `map`, in that order, both top-right.
 *
 * Call this before adding the `NavigationControl` so the search field ends up above it.
 *
 * Each control is built independently and defensively. `MapboxGeocoder.onAdd` parses the access
 * token and **throws synchronously** on a malformed one — and this runs first in the dialog's
 * `map.on('load')` block, ahead of the draw tools and the GeoJSON import. Letting that escape
 * would turn a broken search box into a dead map editor, taking the drawing and upload paths
 * (the ones that actually matter for a DDS) down with it.
 */
export function addMapSearchControls(map: mapboxgl.Map, opts: MapSearchControlsOptions): MapSearchControlsHandle {
  const texts: MapSearchRenderedTexts = { ...MAP_SEARCH_FALLBACK_TEXTS, ...(opts.texts ?? {}) };
  const proximity = toProximity(opts.center);
  const cleanups: Array<() => void> = [];

  let geocoder: MapboxGeocoder | null = null;
  try {
    geocoder = new MapboxGeocoder({
      accessToken: opts.accessToken,
      // The dialog already owns a draggable site marker; a second one would be read as a result.
      marker: false,
      flyTo: true,
      zoom: SEARCH_RESULT_ZOOM,
      placeholder: texts.placeholder,
      localGeocoder: (query: string) => coordinateSearchResults(query) as unknown as MapboxGeocoder.Result[],
      localGeocoderOnly: false,
      // An explicit proximity is only honoured with tracking off: the plugin's _updateProximity()
      // runs on add and nulls it below zoom 9 — where this dialog opens.
      ...(proximity ? { proximity, trackProximity: false } : { trackProximity: true }),
      ...(opts.language ? { language: opts.language } : {}),
    });
    translateGeocoderMessages(geocoder, texts);

    const onGeocoderError = () => opts.onError(MAP_SEARCH_TEXT_KEYS.searchFailed);
    geocoder.on('error', onGeocoderError);

    map.addControl(geocoder, 'top-right');
    // Stable selector for screenshot and accessibility-oriented E2E steps.
    const geocoderInput = document.querySelector('.mapboxgl-ctrl-geocoder input');
    geocoderInput?.setAttribute('data-testid', 'map-search-field');

    const attached = geocoder;
    cleanups.push(() => attached.off('error', onGeocoderError));
    cleanups.push(() => map.removeControl(attached));
  } catch (err) {
    console.error('Map search field could not be added', err);
    geocoder = null;
    opts.onError(MAP_SEARCH_TEXT_KEYS.searchUnavailable);
  }

  let geolocate: mapboxgl.GeolocateControl | null = null;
  try {
    geolocate = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true, timeout: 10_000 },
      fitBoundsOptions: { maxZoom: SEARCH_RESULT_ZOOM },
      // Button, not a toggle: one click, one fix, no background watch — and no prompt on open.
      trackUserLocation: false,
      showUserLocation: true,
      showAccuracyCircle: true,
      ...(opts.geolocation ? { geolocation: opts.geolocation } : {}),
    });

    // A denied permission or a timeout otherwise leaves the button spinning with no explanation.
    const onGeolocateError = (err: unknown) => opts.onError(geolocationErrorKey(err));
    geolocate.on('error', onGeolocateError);

    map.addControl(geolocate, 'top-right');

    const attached = geolocate;
    cleanups.push(() => attached.off('error', onGeolocateError));
    cleanups.push(() => map.removeControl(attached));
  } catch (err) {
    console.error('Map location button could not be added', err);
    geolocate = null;
    opts.onError(MAP_SEARCH_TEXT_KEYS.locationFailed);
  }

  return {
    geocoder,
    geolocate,
    remove() {
      for (const cleanup of cleanups) {
        try {
          cleanup();
        } catch {
          /* already detached, or removed together with the map */
        }
      }
    },
  };
}
